import { inject, Injectable } from "@angular/core";
import { HttpErrorResponse, type HttpParams } from "@angular/common/http";
import { SessionUserDataService } from "@shared/services/user-data/session-user-data.service";
import { DownloadedCoursesService } from "@shared/services/offline/downloaded-courses.service";
import { DownloadedRoutesService } from "@shared/services/offline/downloaded-routes.service";
import { DownloadRecordsService } from "@shared/services/offline/download-records.service";
import { OfflineDatabaseService } from "@shared/services/offline/offline-database.service";
import type { OfflineResource } from "@core/consts/offline-resource.constants";
import type { CourseDownloadRecord } from "@core/models/course-download.model";
import { OfflineResourceTypeEnum } from "@core/enums/offline-resource-type.enum";
import type { CourseDetail } from "@core/models/course-detail.model";
import type {
  LearningRouteDetailData,
  LearningRouteDetailResponse,
  LearningRoutesListResponse,
} from "@core/models/learning-route.model";
import type { LearningRouteCourseModel } from "@core/models/learning-route-course.model";
import type { PaginationModel } from "@core/models/pagination.model";
import type { ResponseDataModel } from "@core/models/response-data.model";

@Injectable({
  providedIn: "root",
})
export class OfflineResponsesService {
  private readonly session: SessionUserDataService = inject(SessionUserDataService);
  private readonly courses: DownloadedCoursesService = inject(DownloadedCoursesService);
  private readonly routes: DownloadedRoutesService = inject(DownloadedRoutesService);
  private readonly downloadRecords: DownloadRecordsService = inject(DownloadRecordsService);
  private readonly offlineDatabase: OfflineDatabaseService = inject(OfflineDatabaseService);

  async respond(resource: OfflineResource, params: HttpParams): Promise<unknown> {
    const uid: string | null = this.session.storedUid();
    if (!uid) throw notDownloaded();

    switch (resource.type) {
      case OfflineResourceTypeEnum.CATALOG:
        return this.catalog(uid, params);
      case OfflineResourceTypeEnum.ROUTES:
        return this.routeList(uid);
      case OfflineResourceTypeEnum.ROUTE_COURSES:
        return this.routeCourses(uid, resource.id, params);
      case OfflineResourceTypeEnum.COURSE:
        return success(found(await this.courses.get(uid, resource.id)));
    }
  }

  async refresh(resource: OfflineResource, body: unknown): Promise<void> {
    const uid: string | null = this.session.storedUid();
    if (!uid) return;

    try {
      if (resource.type === OfflineResourceTypeEnum.COURSE) {
        const course: CourseDetail = (body as ResponseDataModel<CourseDetail>).data;
        const record: CourseDownloadRecord | undefined = await this.downloadRecords.get(uid, resource.id);
        if (record && course.updatedAt === record.updatedAt) await this.courses.save(await this.offlineDatabase.write(), uid, course);
      }
    } catch (error: unknown) {
      console.error("[offline] No se pudo actualizar la copia descargada", resource, error);
    }
  }

  private async catalog(uid: string, params: HttpParams): Promise<ResponseDataModel> {
    const courses: CourseDetail[] = (await this.courses.list(uid)).filter(
      (course: CourseDetail) => !course.learningRouteId && inLearningArea(course, params) && matches(course, params),
    );

    return success({ courses, pagination: singlePage(courses.length) });
  }

  private async routeList(uid: string): Promise<LearningRoutesListResponse> {
    const [pages, courses]: [LearningRouteDetailData[], CourseDetail[]] = await Promise.all([
      this.routes.list(uid),
      this.courses.list(uid),
    ]);

    const items: LearningRoutesListResponse["data"]["items"] = pages
      .filter((page: LearningRouteDetailData) => page.learningRouteCourses.pagination.currentPage === 1)
      .map(({ learningRoute }: LearningRouteDetailData) => ({
        ...learningRoute,
        coursesCount: courses.filter((course: CourseDetail) => course.learningRouteId === learningRoute.id).length,
      }))
      .filter((route) => route.coursesCount > 0);

    return { success: true, data: { items, pagination: singlePage(items.length) } };
  }

  private async routeCourses(uid: string, id: number, params: HttpParams): Promise<LearningRouteDetailResponse> {
    const [pages, courses]: [LearningRouteDetailData[], CourseDetail[]] = await Promise.all([
      this.routes.pages(uid, id),
      this.courses.list(uid),
    ]);
    if (!pages.length) throw notDownloaded();
    const downloaded: Set<number> = new Set(courses.map((course: CourseDetail) => course.id));

    const available: LearningRouteCourseModel[] = pages
      .flatMap((page: LearningRouteDetailData) => page.learningRouteCourses.items)
      .filter((course: LearningRouteCourseModel) => downloaded.has(course.id));
    const items: LearningRouteCourseModel[] = available.filter((course) => matches(course, params));

    return {
      success: true,
      data: {
        learningRoute: { ...pages[0].learningRoute, coursesCount: available.length },
        learningRouteCourses: { items, pagination: singlePage(items.length) },
      },
    };
  }
}

function matches(course: { name: string; progress: number }, params: HttpParams): boolean {
  const name: string = params.get("name")?.trim().toLocaleLowerCase() ?? "";
  if (name && !course.name.toLocaleLowerCase().includes(name)) return false;

  const status: string | null = params.get("progressStatus");
  if (status === "completed") return course.progress === 100;
  if (status === "pending") return course.progress === 0;
  if (status === "in_progress") return course.progress > 0 && course.progress < 100;

  return true;
}

function inLearningArea(course: CourseDetail, params: HttpParams): boolean {
  const areaId: string = String(course.learningArea.id);
  const onlyAreaId: string | null = params.get("learningAreaId");

  return (onlyAreaId === null || onlyAreaId === areaId) && params.get("excludeLearningAreaId") !== areaId;
}

function success<T>(data: T): ResponseDataModel<T> {
  return { message: { title: "Success" }, data };
}

function singlePage(total: number): PaginationModel {
  return { totalRecords: total, currentPage: 1, recordsCurrentPage: total, totalPages: 1, nextPage: null, prevPage: null };
}

function found<T>(record: T | undefined): T {
  if (!record) throw notDownloaded();
  return record;
}

function notDownloaded(): HttpErrorResponse {
  return new HttpErrorResponse({ status: 0, statusText: "No descargado" });
}
